import { GsFormFieldColor, GsFormFieldObjectSelect, GsFormSchema } from '../../../components/gs-form/interfaces/gs-form-schema';
import { SharedItemOverlayDisplayMode, GsStructTextAlignment, SearchOrderType } from '../../../models';
import { PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA } from './../basics/picture-display-mode.schema';
import { DEFAULT_CONFIG } from '../../../services/config/default.config';
import { ACTIVE_SCHEMA_FIELDS } from './../basics/active.schema';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { borderSchemaFields } from './../basics/border.schema';
import { buttonSchemaFields } from './../basics/button-fields.schema';
import { generatePaginatedSchemaFields } from './../basics/paginated.schema';
import { teaserBadgeSchemaFields } from './../basics/teaser-badge.schema';
import { SPACING_SCHEMA } from './spacing.schema';

/**
 * The schema for the search component form.
 * Defines the fields to display for the form.
 */

export const SEARCH_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...ACTIVE_SCHEMA_FIELDS,
    ...BASIC_SCHEMA_FIELDS,
    {
      formControlName: 'search',
      type: 'control',
      default: '{q}'
    },
    ...SPACING_SCHEMA,
    // The search is always a grid, so infinite scroll is offered without a mode condition
    ...generatePaginatedSchemaFields(true),
    {
      formControlName: 'maxMobileInactiveFilters',
      label: 'components.search.schema.maxMobileInactiveFilters.label',
      description: 'components.search.schema.maxMobileInactiveFilters.description',
      type: 'number',
      default: 2,
      min: 0,
      max: 10,
      groupingName: 'components.search.schema.mobileFilters'
    },
    ...teaserBadgeSchemaFields({
      translationRoot: 'components.search.schema',
      groupingName: 'components.search.schema.itemTeaserOptions'
    }),
    {
      formControlName: 'showDateOnImage',
      label: 'components.search.schema.showDateOnImage',
      type: 'checkbox',
      default: true,
      groupingName: 'components.search.schema.itemTeaserOptions'
    },
    {
      formControlName: 'newForDays',
      label: 'components.search.schema.newForDays',
      type: 'number',
      default: 10,
      min: 1,
      groupingName: 'components.search.schema.itemTeaserOptions'
    },
    {
      formControlName: 'itemTextAlignment',
      label: 'schema.alignment.text.name',
      type: 'select',
      valueType: 'number',
      default: GsStructTextAlignment.left,
      groupingName: 'components.search.schema.text.name',
      options: [
        { text: 'schema.alignment.text.left', value: GsStructTextAlignment.left },
        { text: 'schema.alignment.text.center', value: GsStructTextAlignment.center },
        { text: 'schema.alignment.text.right', value: GsStructTextAlignment.right },
        { text: 'schema.alignment.text.justify', value: GsStructTextAlignment.justify }
      ]
    },
    {
      formControlName: 'showItemSubtitle',
      label: 'components.search.schema.text.showItemSubtitle.label',
      type: 'checkbox',
      groupingName: 'components.search.schema.text.name',
      description: 'components.search.schema.text.showItemSubtitle.description',
      default: true
    },
    {
      formControlName: 'shortenItemTitles',
      label: 'components.search.schema.text.shortenItemTitles.label',
      type: 'checkbox',
      groupingName: 'components.search.schema.text.name',
      description: 'components.search.schema.text.shortenItemTitles.description',
      default: false
    },
    {
      formControlName: 'showPhotos',
      label: 'components.search.schema.showPhotos',
      type: 'checkbox',
      groupingName: 'schema.items.name',
      default: true
    },
    {
      formControlName: 'itemImageHeight',
      label: 'schema.items.itemImageHeight',
      type: 'number',
      isResponsive: true,
      default: { default: 200 },
      if: {
        args: [{ formControlName: 'showPhotos', value: true }]
      }
    },
    ...borderSchemaFields('ItemImage', undefined, 'schema.border.name'),
    {
      formControlName: 'showWatchlistButton',
      label: 'components.search.shared.showWatchlistButton',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'showShoppingCartButton',
      label: 'components.search.shared.showShoppingCartButton',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'showDateInTitle',
      label: 'components.search.schema.showDateInTitle',
      type: 'checkbox',
      default: true,
      groupingName: 'components.search.schema.showDate'
    },
    {
      formControlName: 'itemPadding',
      label: 'schema.items.itemPadding',
      type: 'padding',
      useTailwindClasses: true,
      groupingName: 'schema.items.name',
      default: {
        top: 4,
        right: 4,
        bottom: 4,
        left: 4
      }
    },
    {
      formControlName: 'indexWidths',
      label: 'components.search.schema.specialWidths',
      type: 'object-list',
      maxObjectsAmount: 12,
      addObjectLabel: 'components.search.schema.addWidth',
      default: [],
      objectSchema: {
        fields: [
          {
            formControlName: 'index',
            label: 'components.search.schema.position',
            type: 'number',
            labelPosition: 'left',
            min: 1,
            default: 1
          },
          {
            formControlName: 'width',
            label: 'components.search.schema.width',
            type: 'number',
            labelPosition: 'left',
            min: 2,
            max: 10,
            default: 2
          }
        ]
      }
    },
    {
      formControlName: 'itemZoomOnHover',
      label: 'components.image.schema.zoomOnHover',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverText',
      label: 'components.search.shared.itemHoverText',
      type: 'text',
      default: '',
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverTextColor',
      label: 'components.search.shared.itemHoverTextColor',
      type: 'color',
      default: 'secondary-main',
      groupingName: 'schema.hover.name',
      if: {
        args: [{ formControlName: 'itemHoverText', value: '', operator: '!=' }]
      }
    } as GsFormFieldColor,
    {
      formControlName: 'itemShowHoverBackground',
      label: 'components.search.shared.itemShowHoverBackground',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverBackgroundColor',
      label: 'components.search.shared.itemHoverBackgroundColor',
      type: 'color',
      default: 'secondary-main',
      groupingName: 'schema.hover.name',
      if: {
        args: [{ formControlName: 'itemShowHoverBackground', value: true }]
      }
    },
    {
      type: 'object-select',
      label: 'components.search.sortOptions.label',
      formControlName: 'uIAvailableSortOrderOptions',
      default: [{ value: SearchOrderType.Relevance.toString(), text: 'components.search.sortOptions.sortRelevance' }],
      multiple: true,
      options: [
        { value: SearchOrderType.Relevance.toString(), text: 'components.search.sortOptions.sortRelevance' },
        { value: SearchOrderType.Title.toString(), text: 'components.search.sortOptions.sortName1' },
        { value: SearchOrderType.MinKeyPrice.toString(), text: 'components.search.sortOptions.sortPriceAsc' },
        { value: SearchOrderType.Priority.toString(), text: 'components.search.sortOptions.sortPriority' },
        { value: SearchOrderType.Title2.toString(), text: 'components.search.sortOptions.sortName2' },
        { value: SearchOrderType.MaxKeyPrice.toString(), text: 'components.search.sortOptions.sortPriceDesc' },
        { value: SearchOrderType.CreatedOn.toString(), text: 'components.search.sortOptions.sortNew' },
        { value: SearchOrderType.ModifiedOn.toString(), text: 'components.search.sortOptions.sortModified' },
        { value: SearchOrderType.Custom.toString(), text: 'components.search.sortOptions.sortCustom' },
        { value: SearchOrderType.Custom2.toString(), text: 'components.search.sortOptions.sortCustom2' },
        { value: SearchOrderType.TopArticles.toString(), text: 'components.search.sortOptions.sortBestseller' },
        { value: SearchOrderType.Height.toString(), text: 'components.search.sortOptions.sortHeight' },
        { value: SearchOrderType.Circumference.toString(), text: 'components.search.sortOptions.sortCircumference' },
        { value: SearchOrderType.CircumferenceHeight.toString(), text: 'components.search.sortOptions.sortCircumferenceHeight' },
        { value: SearchOrderType.LatestDate.toString(), text: 'components.search.sortOptions.sortDateDesc' },
        { value: SearchOrderType.EarliestDate.toString(), text: 'components.search.sortOptions.sortDateAsc' }
      ]
    } as GsFormFieldObjectSelect,
    {
      formControlName: 'showItemButton',
      label: 'components.search.shared.showItemButton',
      type: 'checkbox',
      default: false,
      groupingName: 'components.search.shared.itemButton'
    },
    {
      formControlName: 'itemButtonTextByArticleGroup',
      label: 'schema.items.itemButtonTextByArticleGroup.label',
      description: 'schema.items.itemButtonTextByArticleGroup.description',
      type: 'checkbox',
      default: false,
      groupingName: 'components.search.shared.itemButton',
      if: {
        args: [{ formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemButtonText',
      label: 'components.search.shared.itemButtonText',
      type: 'text',
      default: '',
      placeholder: 'components.search.shared.itemButtonTextPlaceholder',
      groupingName: 'components.search.shared.itemButton',
      if: {
        args: [{ formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemOverlayDisplayMode',
      label: 'components.search.schema.itemOverlayDisplayMode.label',
      description: 'components.search.schema.itemOverlayDisplayMode.description',
      type: 'select',
      valueType: 'number',
      default: SharedItemOverlayDisplayMode.onlyWithText,
      options: [
        {
          text: 'components.search.schema.itemOverlayDisplayMode.options.never',
          value: SharedItemOverlayDisplayMode.never
        },
        {
          text: 'components.search.schema.itemOverlayDisplayMode.options.onlyWithText',
          value: SharedItemOverlayDisplayMode.onlyWithText
        },
        {
          text: 'components.search.schema.itemOverlayDisplayMode.options.always',
          value: SharedItemOverlayDisplayMode.always
        }
      ]
    },
    ...PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA,
    {
      formControlName: 'buttons',
      label: 'components.search.schema.buttons',
      type: 'group',
      expandable: true,
      default: DEFAULT_CONFIG.uiElements?.buttons ?? {},
      fields: buttonSchemaFields()
    }
  ]
};
